import { Card, CardContent, CardHeader, Skeleton } from '@/components/ui'
import { useTransactions } from '@/modules/projects'
import { cn } from '@/utils'
import { ProcessingCard } from './overview/ProcessingCard'
import { usePortfolioDashboard } from './portfolio/usePortfolioDashboard'

const MASTHEAD_FIGURES = ['accounts', 'statements', 'pages']
const PORTFOLIO_FIGURES = ['cost-basis', 'accounts', 'reconciled']
const MONEY_FIGURES = ['in', 'out', 'net']
const LEGEND_ROWS = ['first', 'second']

// The names vary in length because category and asset-class names do.
const ALLOCATION_ROWS = [
  { key: 'first', name: 'w-24' },
  { key: 'second', name: 'w-44' },
  { key: 'third', name: 'w-48' },
  { key: 'fourth', name: 'w-32' },
  { key: 'fifth', name: 'w-36' },
]

// Mirrors ProjectOverviewPage section for section, including which sections that page skips:
// portfolio only shows for cases with brokerage accounts, and the cash-flow sections only when
// the case has transactions. Both read the same hooks the page does, so nothing shifts when the
// real content lands.
export const OverviewSkeleton = () => {
  const transactions = useTransactions()
  const { accounts: portfolioAccounts } = usePortfolioDashboard()

  const showCashFlowSections =
    transactions.length > 0 || portfolioAccounts.length === 0

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-5">
      <ProcessingCard className="p-6">
        <div className="@container">
          <div className="flex flex-col gap-6 @3xl:flex-row @3xl:items-center @3xl:justify-between">
            <div className="flex flex-col items-start">
              <Skeleton className="h-5 w-24" />
              <Skeleton className="mt-1 h-8 w-64" />
              <Skeleton className="mt-2 h-6 w-56" />
              <Skeleton className="mt-2 h-5 w-48" />
            </div>

            <div className="flex flex-wrap items-center divide-x divide-border">
              {MASTHEAD_FIGURES.map((figure) => (
                <div
                  key={figure}
                  className="flex flex-col items-start px-7 first:pl-0 last:pr-0"
                >
                  <Skeleton className="h-5 w-20" />
                  <Skeleton className="mt-1 h-8 w-6" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </ProcessingCard>

      <div className="@container">
        <div
          className={cn(
            'grid grid-cols-1 gap-4',
            showCashFlowSections ? '@4xl:grid-cols-3' : '@4xl:grid-cols-2',
          )}
        >
          <RingCardSkeleton />
          <RingCardSkeleton />
          {showCashFlowSections ? <TopCategoriesSkeleton /> : null}
        </div>
      </div>

      {portfolioAccounts.length > 0 ? <PortfolioSkeleton /> : null}
      {showCashFlowSections ? <CaseActivitySkeleton /> : null}
    </div>
  )
}

const FilterRowSkeleton = () => (
  <div className="flex flex-wrap items-center justify-end gap-3">
    <Skeleton className="h-8 w-28 rounded-md" />
  </div>
)

// One row per category: the name, then its bar. Matches OverviewBarRow's 44px rhythm.
const BarRowsSkeleton = () => (
  <div className="flex flex-col gap-1">
    {ALLOCATION_ROWS.map((row) => (
      <div key={row.key} className="flex flex-col gap-1.5 py-1.5">
        <Skeleton className={cn('h-5', row.name)} />
        <Skeleton className="h-1.5 w-full rounded-full" />
      </div>
    ))}
  </div>
)

interface RingCardSkeletonProps {
  hasViewLink?: boolean
}

const RingCardSkeleton = ({ hasViewLink }: RingCardSkeletonProps) => (
  <Card className="flex flex-col">
    <CardHeader className="flex-row items-start justify-between gap-4 space-y-0 pb-4">
      <Skeleton className="h-4 w-24" />
      {hasViewLink ? <Skeleton className="h-4 w-10" /> : null}
    </CardHeader>
    <CardContent className="flex flex-1 items-start">
      <div className="flex w-full flex-col items-center gap-4">
        <Skeleton className="size-31 shrink-0 rounded-full" />
        <div className="flex w-full flex-col gap-2">
          {LEGEND_ROWS.map((row) => (
            <div key={row} className="flex items-center justify-between gap-2">
              <Skeleton className="h-4 w-24" />
              <Skeleton className="h-4 w-8" />
            </div>
          ))}
          <Skeleton className="h-3 w-40" />
        </div>
      </div>
    </CardContent>
  </Card>
)

const TopCategoriesSkeleton = () => (
  <Card className="flex flex-col">
    <CardHeader className="flex-row items-start justify-between gap-4 space-y-0 pb-4">
      <Skeleton className="h-4 w-32" />
      <Skeleton className="h-4 w-10" />
    </CardHeader>
    <CardContent>
      <BarRowsSkeleton />
    </CardContent>
  </Card>
)

const PortfolioSkeleton = () => (
  <div className="space-y-3">
    <FilterRowSkeleton />
    <Card>
      <CardHeader className="flex-row items-start justify-between gap-4 space-y-0 pb-4">
        <Skeleton className="h-4 w-20" />
        <Skeleton className="h-4 w-10" />
      </CardHeader>
      <CardContent>
        <div className="@container">
          <div className="grid grid-cols-1 gap-6 @2xl:grid-cols-2">
            <div className="flex flex-col items-start">
              <Skeleton className="h-9 w-48" />
              <Skeleton className="mt-1.5 h-5 w-56" />
              <div className="mt-4 flex flex-wrap divide-x divide-border">
                {PORTFOLIO_FIGURES.map((figure) => (
                  <div
                    key={figure}
                    className="flex flex-col items-start px-6 first:pl-0 last:pr-0"
                  >
                    <Skeleton className="h-5 w-16" />
                    <Skeleton className="mt-0.5 h-7 w-20" />
                  </div>
                ))}
              </div>
            </div>

            <div className="flex flex-col items-start">
              <Skeleton className="mb-2 h-5 w-20" />
              <BarRowsSkeleton />
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  </div>
)

const CaseActivitySkeleton = () => (
  <div className="space-y-3">
    <FilterRowSkeleton />
    <ProcessingCard>
      <CardHeader className="pb-4">
        <div className="flex flex-wrap items-start justify-between gap-x-8 gap-y-4">
          <div className="flex min-w-0 flex-1 flex-col items-start gap-1.5">
            <Skeleton className="h-4 w-24" />
            <Skeleton className="h-4 w-full max-w-md" />
            <Skeleton className="h-4 w-3/4 max-w-sm" />
          </div>
          <div className="flex shrink-0 items-start gap-6 sm:gap-8">
            {MONEY_FIGURES.map((figure) => (
              <div key={figure} className="flex flex-col items-end">
                <Skeleton className="h-5 w-16" />
                <Skeleton className="mt-1 h-7 w-24" />
              </div>
            ))}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <Skeleton className="h-80 w-full" />
      </CardContent>
    </ProcessingCard>
  </div>
)
